import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TableServices from "../../services/TableService";
import CompanyConfigurationServices from "../../services/CompanyConfigurationService";
import {
  POSDashboard,
  OrdersScreen,
  TablesScreen,
  OffersScreen,
  OfferDetailsScreen,
  OrderPlacementScreen,
} from "./components";
import { useUserWithCompany } from "hooks/useCompany";

const POS = () => {
  const { companyId } = useUserWithCompany();
  const navigate = useNavigate();
  // Main navigation state
  const [currentScreen, setCurrentScreen] = useState("dashboard"); // dashboard, orders, tables, offers, offerDetails, orderPlacement
  const [initialOrderStatus, setInitialOrderStatus] = useState("all"); // Initial filter status for orders screen

  // Data state
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [editCart, setEditCart] = useState([]);
  const [editOrderId, setEditOrderId] = useState(null);
  const [editOrderNumber, setEditOrderNumber] = useState(null);
  const [orderTypeConfig, setOrderTypeConfig] = useState({
    Dinein: true,
    Takeaway: true,
    Delivery: true,
  });
  const [companyModules, setCompanyModules] = useState({
    POS: true,
    Kitchen: true,
  });
  const [configLoading, setConfigLoading] = useState(true);

  // Fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fetch tables on mount
  useEffect(() => {
    fetchTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!companyId) return;

    const fetchConfigurations = async () => {
      try {
        setConfigLoading(true);
        const [orderResponse, modulesResponse] = await Promise.all([
          CompanyConfigurationServices.getConfigurations(companyId, "order_settings"),
          CompanyConfigurationServices.getConfigurations(companyId, "compnay_modules"),
        ]);

        if (orderResponse?.success) {
          const items =
            orderResponse?.data?.view?.data ??
            orderResponse?.data?.view?.items ??
            orderResponse?.data?.items ??
            [];

          if (items.length > 0) {
            const value = items[0]?.value || {};
            setOrderTypeConfig({
              Dinein: Boolean(value.Dinein),
              Takeaway: Boolean(value.Takeaway),
              Delivery: Boolean(value.Delivery),
            });
          }
        }

        if (modulesResponse?.success) {
          const items =
            modulesResponse?.data?.view?.data ??
            modulesResponse?.data?.view?.items ??
            modulesResponse?.data?.items ??
            [];

          if (items.length > 0) {
            const value = items[0]?.value || {};
            setCompanyModules({
              POS: value.POS !== false,
              Kitchen: value.Kitchen !== false,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching POS configurations:", error);
      } finally {
        setConfigLoading(false);
      }
    };

    fetchConfigurations();
  }, [companyId]);

  // Fullscreen listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Clear selectedOrderId when navigating away from orders screen
  useEffect(() => {
    if (currentScreen !== "orders") {
      setSelectedOrderId(null);
    }
  }, [currentScreen]);

  const fetchTables = async () => {
    try {
      const response = await TableServices.getAll({ companyId: companyId });
      if (response && response.data?.view?.data) {
        setTables(response.data.view.data.filter((t) => t.isActive));
      }
    } catch (err) {
      console.error("Error fetching tables:", err);
      toast.error("Failed to load tables");
    }
  };

  const handleTableClick = (table) => {
    if (!orderTypeConfig.Dinein) {
      toast.warning("Dine-in order type is disabled in company settings");
      return;
    }
    setSelectedTable(table);
    setCurrentScreen("orderPlacement");
  };

  const handleCreateDineIn = () => {
    if (!orderTypeConfig.Dinein) {
      toast.warning("Dine-in order type is disabled in company settings");
      return;
    }
    setCurrentScreen("tables");
  };

  const handleCreateTakeaway = () => {
    if (!orderTypeConfig.Takeaway) {
      toast.warning("Takeaway order type is disabled in company settings");
      return;
    }
    setSelectedTable(null);
    setCurrentScreen("orderPlacement");
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const fullscreenLabel = isFullscreen ? "Exit Fullscreen" : "Fullscreen";

  useEffect(() => {
    if (configLoading) return;
    if (!companyModules.POS) {
      toast.info("POS module is disabled in Company Configuration");
      navigate("/dashboard", { replace: true });
    }
  }, [companyModules.POS, configLoading, navigate]);

  if (!configLoading && !companyModules.POS) return null;

  // Common props for all screens
  const commonProps = {
    isFullscreen,
    toggleFullscreen,
    fullscreenLabel,
    onHomeClick: () => navigate("/dashboard"),
    onPOSClick: () => setCurrentScreen("dashboard"),
  };

  // Render based on current screen
  switch (currentScreen) {
    case "dashboard":
      return (
        <POSDashboard
          {...commonProps}
          mainTab={0}
          setMainTab={(tab) => {
            if (tab === 1) setCurrentScreen("orders");
          }}
          onCreateDineIn={handleCreateDineIn}
          onCreateTakeaway={handleCreateTakeaway}
          orderTypeConfig={orderTypeConfig}
          tables={tables}
          onViewAllOrders={() => setCurrentScreen("orders")}
          onViewAllTables={() => setCurrentScreen("tables")}
          onSelectOrder={(order) => {
            console.log("Selected order:", order);
            setSelectedOrderId(order.id);
            setCurrentScreen("orders");
          }}
          onSelectTable={handleTableClick}
          handleTablesClick={() => setCurrentScreen("tables")}
          onFilterByStatus={(status) => {
            setInitialOrderStatus(status);
            setCurrentScreen("orders");
          }}
        />
      );

    case "orders":
      return (
        <OrdersScreen
          {...commonProps}
          mainTab={1}
          setMainTab={(tab) => {
            if (tab === 0) setCurrentScreen("dashboard");
          }}
          handleBackToTableSelect={() => setCurrentScreen("tables")}
          handleTablesClick={() => setCurrentScreen("tables")}
          handleOffersClick={() => setCurrentScreen("offers")}
          initialStatus={initialOrderStatus}
          onStatusChange={(status) => setInitialOrderStatus(status)}
          initialOrderId={selectedOrderId}
          onEditOrder={(cart, table, existingOrderId, existingOrderNumber) => {
            setSelectedTable(table || null);
            setEditCart(cart || []);
            setEditOrderId(existingOrderId || null);
            setEditOrderNumber(existingOrderNumber || null);
            setCurrentScreen("orderPlacement");
          }}
        />
      );

    case "tables":
      return (
        <TablesScreen
          {...commonProps}
          mainTab={2}
          setMainTab={(tab) => {
            if (tab === 0) setCurrentScreen("dashboard");
            if (tab === 1) setCurrentScreen("orders");
          }}
          onBack={() => setCurrentScreen("dashboard")}
          handleTablesClick={() => setCurrentScreen("tables")}
          handleOffersClick={() => setCurrentScreen("offers")}
          onTableClick={handleTableClick}
        />
      );

    case "offers":
      return (
        <OffersScreen
          {...commonProps}
          mainTab={3}
          setMainTab={(tab) => {
            if (tab === 0) setCurrentScreen("dashboard");
            if (tab === 1) setCurrentScreen("orders");
          }}
          onBack={() => setCurrentScreen("dashboard")}
          handleTablesClick={() => setCurrentScreen("tables")}
          handleOffersClick={() => setCurrentScreen("offers")}
          onOfferClick={(offerId) => {
            setSelectedOfferId(offerId);
            setCurrentScreen("offerDetails");
          }}
        />
      );

    case "offerDetails":
      return (
        <OfferDetailsScreen
          {...commonProps}
          offerId={selectedOfferId}
          onBack={() => setCurrentScreen("offers")}
        />
      );

    case "orderPlacement":
      return (
        <OrderPlacementScreen
          {...commonProps}
          mainTab={2}
          setMainTab={(tab) => {
            if (tab === 0) setCurrentScreen("dashboard");
            if (tab === 1) setCurrentScreen("orders");
          }}
          selectedTable={selectedTable}
          onBack={() => {
            const returningToOrder = Boolean(editOrderId);
            const returnOrderId = editOrderId;
            setEditCart([]);
            setEditOrderId(null);
            setEditOrderNumber(null);
            if (returningToOrder) {
              setSelectedOrderId(returnOrderId);
              setCurrentScreen("orders");
            } else {
              setCurrentScreen("dashboard");
            }
          }}
          handleTablesClick={() => setCurrentScreen("tables")}
          handleOffersClick={() => setCurrentScreen("offers")}
          tables={tables}
          onSelectTable={handleTableClick}
          initialCart={editCart}
          existingOrderId={editOrderId}
          existingOrderNumber={editOrderNumber}
          orderTypeConfig={orderTypeConfig}
        />
      );

    default:
      return (
        <POSDashboard
          {...commonProps}
          mainTab={0}
          setMainTab={(tab) => {
            if (tab === 1) setCurrentScreen("orders");
          }}
          onCreateDineIn={handleCreateDineIn}
          onCreateTakeaway={handleCreateTakeaway}
          orderTypeConfig={orderTypeConfig}
          tables={tables}
          onViewAllOrders={() => setCurrentScreen("orders")}
          onViewAllTables={() => setCurrentScreen("tables")}
          onSelectOrder={(order) => {
            console.log("Selected order:", order);
            setSelectedOrderId(order.id);
            setCurrentScreen("orders");
          }}
          onSelectTable={handleTableClick}
          handleTablesClick={() => setCurrentScreen("tables")}
          handleOffersClick={() => setCurrentScreen("offers")}
        />
      );
  }
};

export default POS;
